import React, { useEffect, useState } from 'react';
import { mapProps } from 'recompose';
import { graphql, compose, withApollo } from 'react-apollo';
import { gql } from 'graphql.macro';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowDropDown } from '@material-ui/icons';
import CommentIcon from '@mui/icons-material/Comment';

import { getIdFromUrlParams } from 'selectors/assessment/selector';
import Tooltip from 'components/common/Tooltip/Tooltip';
import Header from 'components/common/Header/Header';
import LoadingPage from 'components/common/Loader/Spinner/Spinner';
import { getCurrentUser, listCommentsForField, removeComment } from 'graphql/operations';
import { useViewport } from 'utils/getViewport/getViewport';
import { showErrorMessage, showSuccessMessage } from 'components/common/Alert/Alert';
import { FieldTypes } from '../Answers/helper';
import CommentsModal from './CommentsModal';
import { Comment } from './Comment';
import { CommentInput } from './CommentInput';

const useStyles = makeStyles(theme => ({
  root: {
    margin: '0 0 0.5rem',
    '&:focus': {
      outlineColor: theme.palette.outlineColor
    },
    '&:active': {
      outlineColor: theme.palette.outlineColor
    }
  },
  commentsHeader: {
    color: '#616161'
  },
  collapseIcon: {
    cursor: 'pointer',
    marginBottom: '2px',
    '&.expanded': {
      transform: 'rotate(0deg)'
    },
    '&.collapsed': {
      transform: 'rotate(270deg)'
    },
    '&:focus': {
      outlineColor: theme.palette.outlineColor
    },
    '&:active': {
      outlineColor: theme.palette.outlineColor
    }
  },
  commentIcon: {
    height: '20px !important',
    width: '20px !important'
  },
  commentsTitle: {
    margin: '0 0.5rem',
    fontWeight: '500'
  },
  showComments: {
    padding: '0 2rem',
    color: theme.palette.linkColor,
    cursor: 'pointer',
    fontWeight: '500',
    marginTop: theme.whitespace.least
  },
  commentContainer: {
    margin: '0.5rem 3rem 0 2rem',
    borderTop: '1px solid lightgrey'
  },
  comment: {
    borderTop: '1px solid lightgrey',
    padding: '1rem 0 0'
  }
}));

export const CommentsBase = props => {
  const {
    apolloClient,
    assessmentId,
    translate,
    question,
    fieldAclAction,
    commentMetadata,
    getCurrentUser: currentUser,
    isAssessmentReadOnly
  } = props;
  const { commentsCount, id: fieldId } = question;

  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [filteredComments, setFilteredComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const initialNoOfComments = 5;
  const classes = useStyles();
  const { isMobile } = useViewport();

  let fragmentTypename = '';
  switch (question.fieldType) {
    case FieldTypes.ATTACHMENT_FIELD:
      fragmentTypename = 'AttachmentField';
      break;
    case FieldTypes.TEXT_FIELD:
      fragmentTypename = 'TextField';
      break;
    case FieldTypes.NUMERIC_FIELD:
      fragmentTypename = 'NumericField';
      break;
    case FieldTypes.VALUES_LIST_FIELD: {
      fragmentTypename = 'ValuesListField';
      break;
    }
    case FieldTypes.DATE_FIELD: {
      fragmentTypename = 'DateField';
      break;
    }
    default:
      fragmentTypename = '';
  }

  const getCommentsForField = async () => {
    setIsLoading(commentsCount > 0);
    const { data } = await apolloClient.query({
      query: listCommentsForField,
      variables: {
        assessmentId,
        fieldId
      }
    });
    setComments(data.listCommentsForField);
    setIsLoading(false);
  };

  const toggleCommentsSection = () => {
    setShowComments(!showComments);
    !showComments && !!commentsCount && getCommentsForField();
  };

  const readCommentsStateFromCache = () => {
    const prevFragmentValue = apolloClient.cache.readFragment({
      id: fieldId,
      fragment: gql`
        fragment PrevCommentState on CommentState {
          expanded
        }
      `
    });
    prevFragmentValue?.expanded && toggleCommentsSection();
  };

  const writeCommentsStateToCache = expanded => {
    apolloClient.cache.writeFragment({
      id: fieldId,
      fragment: gql`
        fragment CommentState on CommentState {
          expanded
          __typename
        }
      `,
      data: {
        expanded,
        __typename: 'CommentState'
      }
    });
  };

  const updateCommentsQueryCache = updatedComments => {
    apolloClient.cache.writeQuery({
      query: listCommentsForField,
      variables: {
        assessmentId,
        fieldId
      },
      data: {
        listCommentsForField: updatedComments
      }
    });
  };

  const updateFieldFragment = updatedCommentsCount => {
    apolloClient.cache.writeFragment({
      id: `${fragmentTypename}:${fieldId}`,
      fragment: gql`
        fragment FieldFragment on Field {
          commentsCount
          __typename
        }
      `,
      data: {
        commentsCount: updatedCommentsCount,
        __typename: fragmentTypename
      }
    });
  };

  useEffect(() => {
    !isMobile && readCommentsStateFromCache();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const toggleHandler = () => {
    toggleCommentsSection();
    !isMobile && writeCommentsStateToCache(!showComments);
  };

  const onKeyPressToggle = e => {
    if (e.key === 'Enter') {
      toggleHandler();
    }
  };

  useEffect(() => {
    showComments && setFilteredComments(comments?.slice(0, initialNoOfComments));
  }, [comments, showComments]);

  const showAllCommentsHandler = () => setFilteredComments(comments);

  const updateCommentsState = comment => {
    const newCommentsState = [...comments];
    if (comments.some(c => c.id === comment.id)) {
      newCommentsState[newCommentsState.findIndex(x => x.id === comment.id)] = comment;
    } else {
      newCommentsState.unshift(comment);
    }
    setComments(newCommentsState);
    updateCommentsQueryCache(newCommentsState);
    updateFieldFragment(newCommentsState.length);
  };

  const removeCommentFromState = comment => {
    const newCommentsState = [...comments];
    const objWithIdIndex = newCommentsState.findIndex(c => c.id === comment.id);
    objWithIdIndex > -1 && newCommentsState.splice(objWithIdIndex, 1);
    setComments(newCommentsState);
    updateCommentsQueryCache(newCommentsState);
    updateFieldFragment(newCommentsState.length);
  };

  const removeCommentMethod = async comment => {
    const commentId = comment.id;
    try {
      const { data } = await apolloClient.mutate({
        mutation: removeComment,
        variables: {
          commentId,
          assessmentId
        }
      });
      if (data.removeComment.commentId) {
        removeCommentFromState(comment);
      }
      showSuccessMessage(translate('comments.deleteComment.successMsg'));
    } catch {
      showErrorMessage(translate('comments.deleteComment.errorMsg'));
    }
  };

  const commentHeaderTitle = (
    <div className={classes.commentsHeader}>
      {!isMobile && (
        <ArrowDropDown
          className={`${classes.collapseIcon} ${showComments ? 'expanded' : 'collapsed'}`}
          tabIndex={0}
          onClick={toggleHandler}
          onKeyPress={onKeyPressToggle}
        />
      )}
      <Tooltip title={translate('comments.title')}>
        <CommentIcon className={classes.commentIcon} />
      </Tooltip>
      <span className={classes.commentsTitle}>
        {translate('comments.title')} {commentsCount ? `(${commentsCount})` : ''}
      </span>
    </div>
  );

  const loader = !isMobile && isLoading && <LoadingPage size="md" expanded isNotFullScreen />;

  const renderComments = (
    <div className={classes.commentContainer}>
      <CommentInput
        commentMetadata={commentMetadata}
        sourceFieldId={fieldId}
        updateCommentsState={updateCommentsState}
        isAssessmentReadOnly={isAssessmentReadOnly}
      />
      {filteredComments?.map(comment => (
        <div key={comment.id} className={classes.comment}>
          <Comment
            key={comment.id}
            comment={comment}
            commentMetadata={commentMetadata}
            sourceFieldId={fieldId}
            updateCommentsState={updateCommentsState}
            removeCommentMethod={removeCommentMethod}
            currentUser={currentUser}
            isAssessmentReadOnly={isAssessmentReadOnly}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={classes.root}
      role="button"
      tabIndex={0}
      onClick={isMobile ? toggleHandler : undefined}
      onKeyPress={isMobile ? toggleHandler : undefined}
    >
      <Header headerClasses="p-0 pt-1" title={commentHeaderTitle} />
      {showComments && (
        <>
          {loader}
          {!isMobile && !isLoading && (
            <React.Fragment>
              {renderComments}
              {filteredComments.length < comments?.length && (
                <div
                  className={classes.showComments}
                  data-testid={`show-all-button-${fieldId}`}
                  onClick={showAllCommentsHandler}
                  onKeyDown={showAllCommentsHandler}
                  role="button"
                  tabIndex={0}
                >
                  {translate('comments.showAllComments')}
                </div>
              )}
            </React.Fragment>
          )}
          {isMobile && (
            <CommentsModal
              isLoading={isLoading}
              toggle={() => setShowComments(false)}
              isOpen={showComments}
              question={question}
              fieldAclAction={fieldAclAction}
              comments={comments}
              commentMetadata={commentMetadata}
              updateCommentsState={updateCommentsState}
              removeCommentMethod={removeCommentMethod}
              currentUser={currentUser}
              isAssessmentReadOnly={isAssessmentReadOnly}
            />
          )}
        </>
      )}
    </div>
  );
};

export const CommentsHOC = compose(
  withRouter,
  withApollo,
  withTranslation(),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  mapProps(props => {
    return {
      translate: props.t,
      history: props.history,
      getCurrentUser: props.getCurrentUser.getCurrentUser,
      apolloClient: props.client,
      assessmentId: getIdFromUrlParams(props),
      question: props.question,
      fieldAclAction: props.fieldAclAction,
      commentMetadata: props.commentMetadata,
      isAssessmentReadOnly: props.isAssessmentReadOnly
    };
  })
);

export const Comments = CommentsHOC(CommentsBase);
